<template>
  <div ref="rootRef" class="message-composer" :class="{
    'is-expanded': showExpanded,
    editing: isEditing
  }">
    <img v-if="avatarUrl" class="message-composer__avatar-sr" :src="avatarUrl" alt="" />

    <div class="message-composer__card">
      <div :class="['message-composer__input-shell', { 'is-empty': !modelValue }]">
        <div v-if="hasAttachments" class="message-composer__attachment-strip">
          <slot name="attachments" />
        </div>

        <div class="message-composer__editor-stack">
          <span v-if="!modelValue" class="message-composer__placeholder-guide"
            :class="{ 'is-collapsed': !showExpanded }" aria-hidden="true">{{ placeholder }}</span>
          <div ref="highlightRef" class="message-composer__highlight" aria-hidden="true" v-html="resolvedHighlightHtml">
          </div>
          <TextArea ref="textareaComponentRef" :model-value="modelValue" class="message-composer__textarea-field"
            :textarea-class="'message-composer__textarea'" :placeholder="placeholder" :rows="1" :auto-resize="false"
            :disabled="disabled" @update:model-value="onModelUpdate" @focus="handleFocus" @blur="handleBlur"
            @input="handleInput" @click="emit('selection-change', $event)" @keydown="handleKeydown"
            @keyup="emit('selection-change', $event)" @scroll="syncScroll" />
        </div>
      </div>

      <div class="message-composer__toolbar-shell" :aria-hidden="showExpanded ? undefined : 'true'">
        <div class="message-composer__toolbar">
          <div class="message-composer__toolbar-left">
            <OverflowMenu v-if="showPlusMenu && !isEditing" :key="plusMenuKey" class="message-composer__plus-menu"
              align="left" button-label="Add message options" trigger-tooltip="Add" :disabled="!canAttach" portal
              placement="top"
              :trigger-class="{ 'message-composer__action': true, 'plus': true, 'message-composer__toolbar-trigger': true, disabled: !canAttach }"
              :options="plusMenuOptions" @open="handlePlusOpen" @select="handlePlusSelect">
              <template #trigger>
                <span class="icon-plus-18" aria-hidden="true"></span>
              </template>
            </OverflowMenu>

            <span v-if="showPlusMenu && !isEditing" class="message-composer__toolbar-divider" aria-hidden="true"></span>

            <button v-if="showMention" type="button" class="message-composer__toolbar-trigger"
              aria-label="Mention someone" data-tippy-content="Mention" data-tippy-placement="top"
              data-tippy-delay="2000,0" :disabled="disabled || sending" @click="emit('mention')">
              <span class="icon-at-18" aria-hidden="true"></span>
            </button>

            <button v-if="showLink" type="button" class="message-composer__toolbar-trigger"
              :aria-label="linkAriaLabel" :data-tippy-content="linkTooltip" data-tippy-placement="top"
              data-tippy-delay="2000,0" :disabled="disabled || sending" @click="emit('link')">
              <span class="icon-link-18" aria-hidden="true"></span>
            </button>

            <button v-if="showAttach" type="button" class="message-composer__toolbar-trigger" aria-label="Attach file"
              data-tippy-content="Attach" data-tippy-placement="top" data-tippy-delay="2000,0" :disabled="!canAttach"
              @click="emit('attach')">
              <span class="icon-paperclip-18" aria-hidden="true"></span>
            </button>
          </div>

          <div class="message-composer__toolbar-right message-composer__actions">
            <template v-if="isEditing">
              <Button type="button" class="message-composer__action cancel" variant="ghost" tone="neutral" icon-only
                :disabled="disabled || sending" @click="emit('cancel')">
                <i class="material-icons">close</i>
              </Button>
              <Button type="button" :class="['message-composer__action', 'accept', { disabled: isSendDisabled }]"
                variant="soft" tone="primary" icon-only :disabled="isSendDisabled" @click="emit('submit')">
                <i class="material-icons">check</i>
              </Button>
            </template>
            <Button v-else type="button"
              :class="['message-composer__action', 'send', { disabled: isSendDisabled, sending }]" variant="soft"
              tone="neutral" icon-only :disabled="isSendDisabled" :aria-busy="sending ? 'true' : 'false'"
              @click="emit('submit')">
              <svg v-if="sending" class="message-composer__spinner" xmlns="http://www.w3.org/2000/svg" width="16"
                height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
              <span v-else class="icon-up-arrow-16" aria-hidden="true"></span>
            </Button>
          </div>
        </div>
      </div>
    </div>

    <slot name="suggestions" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import Button from '@/components/ui/button/Button.vue';
import OverflowMenu from '@/components/ui/overflow-menu/OverflowMenu.vue';
import type { OverflowMenuOption } from '@/components/ui/overflow-menu/OverflowMenu';
import TextArea from '@/components/ui/text-area/TextArea.vue';

import type { MessageComposerExpose } from './MessageComposer';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type TextAreaExpose = {
  focus: () => void;
  setSelectionRange: (start: number, end: number) => void;
  textareaRef: HTMLTextAreaElement | null;
};

const props = withDefaults(defineProps<{
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  sending?: boolean;
  sendDisabled?: boolean;
  isEditing?: boolean;
  forceExpanded?: boolean;
  hasAttachments?: boolean;
  canAttach?: boolean;
  avatarUrl?: string;
  highlightHtml?: string;
  showPlusMenu?: boolean;
  showMention?: boolean;
  showLink?: boolean;
  showAttach?: boolean;
  linkAriaLabel?: string;
  linkTooltip?: string;
  plusMenuKey?: number | string;
  plusMenuOptions?: OverflowMenuOption[];
}>(), {
  modelValue: '',
  placeholder: 'Add interaction notes',
  disabled: false,
  sending: false,
  sendDisabled: false,
  isEditing: false,
  forceExpanded: false,
  hasAttachments: false,
  canAttach: true,
  avatarUrl: '',
  highlightHtml: '',
  showPlusMenu: true,
  showMention: true,
  showLink: true,
  showAttach: true,
  linkAriaLabel: 'Link shipment reference',
  linkTooltip: 'Link',
  plusMenuKey: 0,
  plusMenuOptions: () => [{
    key: 'upload',
    label: 'Upload file',
    icon: 'upload_file'
  }]
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  submit: [];
  cancel: [];
  mention: [];
  link: [];
  attach: [];
  upload: [];
  input: [event: Event];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
  'selection-change': [event?: MouseEvent | KeyboardEvent];
  'plus-open': [];
}>();

const rootRef = ref<HTMLElement | null>(null);
const highlightRef = ref<HTMLElement | null>(null);
const textareaComponentRef = ref<TextAreaExpose | null>(null);
const isExpanded = ref(false);
let collapseTimeoutId: number | null = null;
let layoutFrame: number | null = null;
const layoutTimeouts: number[] = [];

const hasContent = computed(() => (
  props.modelValue.trim().length > 0 || props.hasAttachments
));

const showExpanded = computed(() => (
  isExpanded.value
  || props.forceExpanded
  || props.isEditing
  || hasContent.value
));

const isSendDisabled = computed(() => (
  props.sendDisabled
  || props.disabled
  || props.sending
  || (!hasContent.value && !props.isEditing)
));

const resolvedHighlightHtml = computed(() => {
  if (props.highlightHtml) {
    return props.highlightHtml;
  }

  if (!props.modelValue) {
    return '';
  }

  return formatPlainHtml(props.modelValue);
});

function formatPlainHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

function getTextareaElement(): HTMLTextAreaElement | null {
  return textareaComponentRef.value?.textareaRef || null;
}

function clearCollapseTimeout(): void {
  if (collapseTimeoutId !== null) {
    window.clearTimeout(collapseTimeoutId);
    collapseTimeoutId = null;
  }
}

function clearScheduledLayout(): void {
  if (layoutFrame !== null) {
    window.cancelAnimationFrame(layoutFrame);
    layoutFrame = null;
  }

  while (layoutTimeouts.length > 0) {
    const timeoutId = layoutTimeouts.pop();
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

function syncOverlayHeight(): void {
  const textarea = getTextareaElement();
  if (!textarea || !highlightRef.value) {
    return;
  }

  highlightRef.value.style.height = `${textarea.offsetHeight}px`;
}

function syncScroll(): void {
  const textarea = getTextareaElement();
  if (!textarea || !highlightRef.value) {
    return;
  }

  highlightRef.value.scrollTop = textarea.scrollTop;
  highlightRef.value.scrollLeft = textarea.scrollLeft;
}

function syncTextareaHeight(): void {
  const textarea = getTextareaElement();
  if (!textarea) {
    return;
  }

  if (!showExpanded.value && !props.modelValue.trim()) {
    textarea.style.height = '';
    textarea.style.overflowY = 'hidden';
    syncOverlayHeight();
    syncScroll();
    return;
  }

  textarea.style.height = 'auto';
  const scrollHeight = textarea.scrollHeight;
  const nextHeight = Math.min(scrollHeight, 120);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = scrollHeight > 120 ? 'auto' : 'hidden';
  syncOverlayHeight();
  syncScroll();
}

function scheduleLayoutSync(): void {
  clearScheduledLayout();

  void nextTick(() => {
    syncTextareaHeight();

    layoutFrame = window.requestAnimationFrame(() => {
      layoutFrame = null;
      syncTextareaHeight();
    });

    for (const delayMs of [80, 220]) {
      const timeoutId = window.setTimeout(() => {
        const index = layoutTimeouts.indexOf(timeoutId);
        if (index >= 0) {
          layoutTimeouts.splice(index, 1);
        }
        syncTextareaHeight();
      }, delayMs);
      layoutTimeouts.push(timeoutId);
    }
  });
}

function expand(): void {
  clearCollapseTimeout();
  isExpanded.value = true;
  void nextTick(() => {
    syncTextareaHeight();
  });
}

function collapse(): void {
  clearCollapseTimeout();
  isExpanded.value = false;

  const textarea = getTextareaElement();
  if (textarea) {
    textarea.style.height = '';
    textarea.style.overflowY = 'hidden';
  }

  syncOverlayHeight();
  syncScroll();
}

function tryCollapse(): void {
  if (props.isEditing || hasContent.value || props.forceExpanded || props.hasAttachments) {
    return;
  }

  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) {
    return;
  }

  collapse();
}

function scheduleCollapseCheck(): void {
  clearCollapseTimeout();
  collapseTimeoutId = window.setTimeout(() => {
    collapseTimeoutId = null;
    tryCollapse();
  }, 0);
}

function onModelUpdate(value: string): void {
  emit('update:modelValue', value);
}

function handleFocus(event: FocusEvent): void {
  expand();
  emit('focus', event);
}

function handleBlur(event: FocusEvent): void {
  scheduleCollapseCheck();
  emit('blur', event);
}

function handleInput(event: Event): void {
  syncTextareaHeight();
  emit('input', event);
}

function handleKeydown(event: KeyboardEvent): void {
  emit('keydown', event);
}

function handlePlusOpen(): void {
  expand();
  emit('plus-open');
}

function handlePlusSelect(actionKey: string): void {
  if (actionKey === 'upload') {
    emit('upload');
  }
}

function focus(): void {
  textareaComponentRef.value?.focus();
}

function setSelectionRange(start: number, end: number): void {
  textareaComponentRef.value?.setSelectionRange(start, end);
}

watch(hasContent, (nextHasContent) => {
  if (nextHasContent) {
    expand();
  }
});

watch(() => props.forceExpanded, (forced) => {
  if (forced) {
    expand();
  } else {
    scheduleCollapseCheck();
  }
});

watch(() => props.modelValue, () => {
  void nextTick(() => {
    syncTextareaHeight();
  });
});

onBeforeUnmount(() => {
  clearCollapseTimeout();
  clearScheduledLayout();
});

defineExpose<MessageComposerExpose>({
  focus,
  setSelectionRange,
  getTextareaElement,
  getRootElement: () => rootRef.value,
  expand,
  collapse,
  syncLayout: scheduleLayoutSync
});
</script>
